const EMBED_PARAM = "embed";
const EMBED_DOMAIN_PARAM = "embed_domain";

let embedReadySent = false;
let resizeDebounceTimer: number | null = null;

export function isEmbedMode(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get(EMBED_PARAM) === "true";
}

export function getEmbedDomain(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const explicit = params.get(EMBED_DOMAIN_PARAM);
  if (explicit) return explicit;

  try {
    if (document.referrer) {
      return new URL(document.referrer).hostname;
    }
  } catch {
    // malformed referrer
  }
  return null;
}

export function isInsideIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export function postToParent(
  type: string,
  payload?: Record<string, unknown>
): void {
  if (!isInsideIframe()) return;
  try {
    window.parent.postMessage({ type, ...payload }, "*");
  } catch {
    // cross-origin restriction — silently ignored
  }
}

export function postResize(): void {
  if (resizeDebounceTimer !== null) {
    window.clearTimeout(resizeDebounceTimer);
  }
  resizeDebounceTimer = window.setTimeout(() => {
    resizeDebounceTimer = null;
    const height = document.documentElement.scrollHeight;
    postToParent("stream:resize", { data: { height } });
  }, 80);
}

export function postReady(): void {
  postToParent("stream:ready");
}

/** Idempotent — parent spinner stays until checkout UI is interactive. */
export function signalEmbedReady(): void {
  if (embedReadySent || !isEmbedMode()) return;
  embedReadySent = true;
  postReady();
  postResize();
}

/** Strip HTML splash before React boots so iframe never flashes Stream logo. */
export function initEmbedShell(): void {
  if (!isEmbedMode()) return;
  document.body.classList.remove("page-loading");
  document.body.style.background = "transparent";
  document.documentElement.style.background = "transparent";
  const splash = document.getElementById("splash-screen");
  if (splash) splash.style.display = "none";
}

export function postRedirect(url: string): void {
  postToParent("stream:redirect", { url });
}

/**
 * Moyasar SDK hardcodes `domain_name: window.location.hostname` when calling
 * Apple Pay's validate-merchant endpoint. Inside an iframe the hostname is the
 * billing domain, but Apple checks the top-level origin. This interceptor
 * rewrites `domain_name` in the POST body to the actual parent domain so
 * Apple Pay merchant validation succeeds in embedded checkouts.
 *
 * Call once before loading the Moyasar script; returns a teardown function.
 */
export function patchApplePayDomain(): (() => void) | undefined {
  const embedDomain = getEmbedDomain();
  if (!embedDomain || !isInsideIframe()) return undefined;

  const originalFetch = window.fetch;

  window.fetch = function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ) {
    if (
      init?.method?.toUpperCase() === "POST" &&
      typeof init.body === "string"
    ) {
      try {
        const body = JSON.parse(init.body);
        if (body.validation_url && body.domain_name) {
          body.domain_name = embedDomain;
          init = { ...init, body: JSON.stringify(body) };
        }
      } catch {
        // not JSON — pass through
      }
    }
    return originalFetch.call(window, input, init);
  };

  return () => {
    window.fetch = originalFetch;
  };
}

let resizeObserver: ResizeObserver | null = null;

export function startResizeObserver(): () => void {
  if (resizeObserver) return () => {};

  postResize();

  resizeObserver = new ResizeObserver(() => {
    postResize();
  });
  resizeObserver.observe(document.body);

  return () => {
    resizeObserver?.disconnect();
    resizeObserver = null;
  };
}
